import { skipToken } from "@reduxjs/toolkit/query";
import type { GroupId } from "@repo/app-types/entities/group";
import type { TeammatePreferences } from "@repo/app-types/entities/teammate";
import { filtersPrefillSchema } from "@repo/app-types/helpers/customerInteractionUrl";
import {
  type FC,
  type ReactNode,
  useCallback,
  useMemo,
  useReducer,
} from "react";
import {
  useMyPerformanceDefaultsQuery,
  useUpdateTeammateMutation,
} from "../../api/entities/teammate.js";
import { useAppSelector } from "../../hooks/hook.js";
import { usePrefillFromUrl } from "../../hooks/usePrefillFromUrl.js";
import { selectTeammate } from "../teammate/teammateSlice.js";
import { FilterContext, type FilterContextValue } from "./filterContext.js";
import { type FilterValues, defaultFilterValues } from "./filterState.js";
import { useCompanyDefaultDateRange } from "./useCompanyDefaultDateRange.js";

export interface FilterProviderProps {
  children?: ReactNode;
}

export const FilterProvider: FC<FilterProviderProps> = (props) => {
  const { filters } = usePrefillFromUrl(filtersPrefillSchema) ?? {};

  // Maintain the state of the filters. Filters that were prefilled start as
  // changed values rather than going through `onChange`, so they aren't saved.
  const {
    changedValues,
    onChange: changeValues,
    onReset,
  } = useChangedValues(filters ?? initialChangedValues);

  // Load teammate's preferred default teammate group ids
  const { defaultTeammateGroupIds, saveDefaultTeammateGroupIds } =
    useDefaultTeammateGroupIds();

  // Save the teammate's group pick as their preferred default.
  const onChange = useCallback(
    (newValues: Partial<FilterValues>) => {
      changeValues(newValues);
      if (newValues.teammateGroupIds !== undefined) {
        saveDefaultTeammateGroupIds(newValues.teammateGroupIds);
      }
    },
    [changeValues, saveDefaultTeammateGroupIds],
  );

  // Honor the company's `performance_default_date_range` setting when set.
  const companyDefaultDateRange = useCompanyDefaultDateRange();

  // Use the teammate preferences to compute the default filter values
  const effectiveDefaultValues: FilterValues = useMemo(
    () => ({
      ...defaultFilterValues,
      dateRange: companyDefaultDateRange ?? defaultFilterValues.dateRange,
      // Optimistically use the changed value, because the expectations is that it will be saved as the teammate's default
      teammateGroupIds:
        changedValues.teammateGroupIds !== undefined
          ? changedValues.teammateGroupIds
          : defaultTeammateGroupIds,
    }),
    [changedValues, companyDefaultDateRange, defaultTeammateGroupIds],
  );

  // Use the defaults and the user's changes to compute the true current filter values
  const effectiveValues: FilterValues = useMemo(
    () => ({
      ...effectiveDefaultValues,
      ...changedValues,
    }),
    [changedValues, effectiveDefaultValues],
  );

  const contextValue = useMemo<FilterContextValue>(
    () => ({
      defaultValues: effectiveDefaultValues,
      values: effectiveValues,
      changedValues,
      onChange,
      onReset,
    }),
    [effectiveDefaultValues, effectiveValues, changedValues, onChange, onReset],
  );

  return (
    <FilterContext.Provider value={contextValue}>
      {props.children}
    </FilterContext.Provider>
  );
};

/** Loads and fetches the user's preferred teammate group ids. */
function useDefaultTeammateGroupIds() {
  const teammate = useAppSelector(selectTeammate);
  const teammateId = teammate?.id;
  const preferences = teammate?.settings.preferences;

  // Infer a default only for a teammate who has never chosen a group filter (a
  // loading teammate counts as "not yet"). Every page that consumes the filter
  // — performance reports, signals and the agent pages, not just the playbook
  // pages — infers it, so the default is the same wherever it is shown.
  const shouldInferDefaults =
    preferences !== undefined &&
    preferences.team_performance_filter_group_ids === undefined;

  // A wanted-but-unstarted query already reports as loading: `useQuery`
  // rewrites its uninitialized state to pending whenever the argument isn't
  // `skipToken`.
  const { data: inferredDefaults, isLoading: areInferredDefaultsLoading } =
    useMyPerformanceDefaultsQuery(shouldInferDefaults ? undefined : skipToken);

  const defaultTeammateGroupIds = computeDefaultTeammateGroupIds({
    preferences,
    inferredGroupIds: inferredDefaults?.teammate_group_ids,
    areInferredDefaultsLoading,
  });

  const [updateTeammate] = useUpdateTeammateMutation();

  const saveDefaultTeammateGroupIds = useCallback(
    async (groupIds: readonly GroupId[] | null) => {
      if (!teammateId) {
        return;
      }

      await updateTeammate({
        id: teammateId,
        patchPayload: {
          preferences: {
            team_performance_filter_group_ids: groupIds
              ? [...groupIds]
              : groupIds,
          },
        },
      }).unwrap();
    },
    [teammateId, updateTeammate],
  );

  return {
    defaultTeammateGroupIds,
    saveDefaultTeammateGroupIds,
  };
}

/**
 * The teammate's default group ids: `undefined` means no choice is available
 * yet, so the inferred default applies, and `null` means all groups.
 */
function computeDefaultTeammateGroupIds(options: {
  preferences: TeammatePreferences | undefined;
  inferredGroupIds: readonly GroupId[] | null | undefined;
  areInferredDefaultsLoading: boolean;
}): readonly GroupId[] | null | undefined {
  const { preferences, inferredGroupIds, areInferredDefaultsLoading } = options;
  if (preferences === undefined) {
    return undefined;
  }
  if (preferences.team_performance_filter_group_ids !== undefined) {
    return preferences.team_performance_filter_group_ids;
  }
  if (areInferredDefaultsLoading) {
    return undefined;
  }
  return inferredGroupIds ?? null;
}

type ValuesChangedAction = {
  type: "changed";
  payload: Partial<FilterValues>;
};

type ValuesResetAction = {
  type: "reset";
};

type ValuesAction = ValuesChangedAction | ValuesResetAction;

const initialChangedValues: Partial<FilterValues> = Object.freeze({});

function reduceChangedValues(
  state: Partial<FilterValues>,
  action: ValuesAction,
): Partial<FilterValues> {
  switch (action.type) {
    case "changed":
      return {
        ...state,
        ...action.payload,
      };

    case "reset":
      return initialChangedValues;
  }
}

function useChangedValues(initialValues: Partial<FilterValues>) {
  const [changedValues, dispatch] = useReducer(
    reduceChangedValues,
    initialValues,
  );

  const onChange = useCallback((newValues: Partial<FilterValues>) => {
    dispatch({ type: "changed", payload: newValues });
  }, []);

  const onReset = useCallback(() => {
    dispatch({ type: "reset" });
  }, []);

  return { changedValues, onChange, onReset };
}
